import { micaStatsService, type MicaStats } from "./mica-stats.service"
import "./tbw-mica-charts.component.scss"

// ── Brand palette ────────────────────────────────────────────────────────────
const ROYAL = "#3442eb"
const NAVY = "#161032"
const MUTED = "#c8c5da"
// Ordered shade scale for the multi-slice sector donut.
const SECTOR_SCALE = ["#3442eb", "#4f48ee", "#6a64f1", "#8781f4", "#a8a3f7", "#2b2570", "#1f1a4d", NAVY]

type Segment = { label: string; sublabel?: string; value: number; color: string }

interface ChartCard {
  eyebrow: string
  title: string
  subtitle: string
  segments: Segment[]
  variant: "donut" | "pie"
  /** Index of the segment whose share is shown big in the donut centre (donut only). */
  centerIndex: number
  centerCaption: string
  footerNote: string
}

const SECTOR_TOP_N = 7

export class TbwMicaChartsComponent extends HTMLElement {
  private get chartKind(): string {
    return (this.getAttribute("chart") || "all").toLowerCase()
  }
  private get type(): string {
    return (this.getAttribute("type") || "casp").toLowerCase()
  }

  async connectedCallback() {
    this.classList.add("tbw-mica")
    this.innerHTML = `<div class="tbw-mica__loading">Loading…</div>`
    try {
      const stats = await micaStatsService.get(this.type)
      this.renderCards(stats)
    } catch {
      this.innerHTML = `<div class="tbw-mica__error">Unable to load the MiCA data right now.</div>`
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  private renderCards(stats: MicaStats) {
    if (this.chartKind === "headline") {
      this.innerHTML = this.headlineHtml(stats)
      this.animateCount()
      return
    }
    if (this.chartKind === "all") {
      // 2×2 on desktop, single column on mobile (handled in CSS).
      const cards = ["sector", "origin", "cost", "geography"].map((k) => this.cardHtml(k, stats)).join("")
      this.innerHTML = `<div class="tbw-mica__grid">${cards}</div>`
    } else {
      this.innerHTML = this.cardHtml(this.chartKind, stats)
    }
    this.wireInteractions()
  }

  // ── Headline: big live counter + top jurisdictions ───────────────────────────
  private headlineHtml(stats: MicaStats): string {
    const total = stats.totalEntities
    const top = [...(stats.topCountries ?? [])].slice(0, 5)
    const max = top.length ? top[0].count : 1
    const rows = top
      .map(
        (c) => `
        <li class="tbw-mica__hl-row">
          <span class="tbw-mica__hl-country">${c.name}</span>
          <span class="tbw-mica__hl-track"><span class="tbw-mica__hl-bar" style="width:${Math.max(
            8,
            Math.round((c.count / max) * 100)
          )}%"></span></span>
          <span class="tbw-mica__hl-count">${c.count}</span>
        </li>`
      )
      .join("")
    return `
      <div class="tbw-mica__headline">
        <div class="tbw-mica__hl-stat">
          <span class="tbw-mica__hl-num" data-count="${total}">0</span>
          <span class="tbw-mica__hl-label">MiCA licences mapped</span>
        </div>
        <div class="tbw-mica__hl-top">
          <p class="tbw-mica__eyebrow">TOP JURISDICTIONS</p>
          <ul class="tbw-mica__hl-list">${rows}</ul>
        </div>
      </div>`
  }

  /** Count up to the target the first time the headline scrolls into view. */
  private animateCount() {
    const el = this.querySelector<HTMLElement>(".tbw-mica__hl-num")
    if (!el) return
    const target = Number(el.getAttribute("data-count") || "0")
    const run = () => {
      const dur = 1200
      let start = 0
      const step = (ts: number) => {
        if (!start) start = ts
        const p = Math.min(1, (ts - start) / dur)
        const eased = 1 - Math.pow(1 - p, 3)
        el.textContent = String(Math.round(eased * target))
        if (p < 1) requestAnimationFrame(step)
        else el.textContent = String(target)
      }
      requestAnimationFrame(step)
    }
    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries, obs) => {
          for (const e of entries)
            if (e.isIntersecting) {
              run()
              obs.disconnect()
              break
            }
        },
        { threshold: 0.4 }
      )
      io.observe(el)
    } else {
      run()
    }
  }

  private cardHtml(kind: string, stats: MicaStats): string {
    if (kind === "cost") return this.costCardHtml(stats)
    const card = this.buildDonutCard(kind, stats)
    if (!card) return ""
    return this.donutCardHtml(card, stats)
  }

  private brandHeader(eyebrow: string, title: string, subtitle: string): string {
    return `
      <div class="tbw-mica__head">
        <p class="tbw-mica__eyebrow">${eyebrow}</p>
        <h3 class="tbw-mica__title">${title}</h3>
        <p class="tbw-mica__subtitle">${subtitle}</p>
      </div>`
  }

  private footer(note: string): string {
    return `
      <div class="tbw-mica__footer">
        <span>${note}</span>
      </div>`
  }

  // ── Donut charts (geography / origin / sector) ───────────────────────────────
  private buildDonutCard(kind: string, stats: MicaStats): ChartCard | null {
    const footerNote = "Source: ESMA MiCA register"

    if (kind === "geography") {
      const g = stats.geography
      const segments: Segment[] = [
        { label: "EU / EEA-based", sublabel: "Home-grown European companies", value: g.euBased, color: ROYAL },
        { label: "Non-EU-based", sublabel: "US, UK, Switzerland, Asia… passporting in", value: g.nonEu, color: NAVY },
      ]
      if (g.unknown > 0) segments.push({ label: "Unknown", value: g.unknown, color: MUTED })
      return {
        eyebrow: "GEOGRAPHY",
        title: "EU-based vs. non-EU entities",
        subtitle: `Where the ${this.fmt(stats.totalEntities)} mapped entities are headquartered relative to the EU.`,
        segments,
        variant: "donut",
        centerIndex: 1,
        centerCaption: "NON-EU",
        footerNote,
      }
    }

    if (kind === "origin") {
      const o = stats.origin
      const segments: Segment[] = [
        { label: "Crypto-native", sublabel: "Exchanges, custodians, DeFi, infra", value: o.cryptoNative, color: ROYAL },
        { label: "Incumbent / TradFi", sublabel: "Banks, brokers, neobanks, asset mgrs", value: o.incumbent, color: NAVY },
      ]
      if (o.unknown > 0) segments.push({ label: "Unclassified", value: o.unknown, color: MUTED })
      return {
        eyebrow: "ORIGIN",
        title: "Crypto-native vs. non crypto-native",
        subtitle: "Split between crypto-first companies and incumbents (banks, fintechs, asset managers) entering the space.",
        segments,
        variant: "donut",
        centerIndex: 0,
        centerCaption: "NATIVE",
        footerNote,
      }
    }

    if (kind === "sector") {
      const segments = this.sectorSegments(stats.sectors)
      return {
        eyebrow: "ACTIVITY",
        title: "Breakdown by sector",
        subtitle: "Distribution of mapped entities across the main MiCA-regulated activities.",
        segments,
        variant: "pie",
        centerIndex: 0,
        centerCaption: "",
        footerNote,
      }
    }

    return null
  }

  /** Top-N sectors + an aggregated "Other" tail. */
  private sectorSegments(sectors: Array<{ name: string; count: number }>): Segment[] {
    const sorted = [...sectors].sort((a, b) => b.count - a.count)
    const head = sorted.slice(0, SECTOR_TOP_N)
    const tail = sorted.slice(SECTOR_TOP_N)
    const segments: Segment[] = head.map((s, i) => ({
      label: s.name,
      value: s.count,
      color: SECTOR_SCALE[i % SECTOR_SCALE.length],
    }))
    const otherTotal = tail.reduce((sum, s) => sum + s.count, 0)
    if (otherTotal > 0) {
      segments.push({
        label: "Other",
        sublabel: tail.slice(0, 3).map((s) => s.name).join(" · "),
        value: otherTotal,
        color: NAVY,
      })
    }
    return segments
  }

  private donutCardHtml(card: ChartCard, _stats: MicaStats): string {
    const total = card.segments.reduce((s, x) => s + x.value, 0) || 1
    const centerSeg = card.segments[card.centerIndex] ?? card.segments[0]
    const centerPct = Math.round((centerSeg.value / total) * 100)

    const chartHtml =
      card.variant === "pie"
        ? `<div class="tbw-mica__chart-wrap">${this.pieSvg(card.segments, total)}</div>`
        : `<div class="tbw-mica__chart-wrap">
            ${this.donutSvg(card.segments, total)}
            <div class="tbw-mica__donut-center">
              <span class="tbw-mica__center-pct">${centerPct}%</span>
              <span class="tbw-mica__center-cap">${card.centerCaption}</span>
            </div>
          </div>`

    return `
      <article class="tbw-mica__card" data-total="${total}">
        ${this.brandHeader(card.eyebrow, card.title, card.subtitle)}
        <div class="tbw-mica__body">
          ${chartHtml}
          <ul class="tbw-mica__legend">
            ${card.segments
              .map((seg, i) => {
                const pct = Math.round((seg.value / total) * 100)
                return `
                <li class="tbw-mica__legend-row" data-index="${i}">
                  <span class="tbw-mica__swatch" style="background:${seg.color}"></span>
                  <span class="tbw-mica__legend-label">
                    <strong>${seg.label}</strong>
                    ${seg.sublabel ? `<em>${seg.sublabel}</em>` : ""}
                  </span>
                  <span class="tbw-mica__legend-count">${this.fmt(seg.value)}</span>
                  <span class="tbw-mica__legend-pct">${pct}%</span>
                </li>`
              })
              .join("")}
          </ul>
        </div>
        ${this.footer(card.footerNote)}
        <div class="tbw-mica__tooltip" hidden></div>
      </article>`
  }

  /** Stacked-arc donut. pathLength=100 lets us drive dash with raw percentages. */
  private donutSvg(segments: Segment[], total: number): string {
    const GAP = 0.7 // % of circumference left white between slices
    let offset = 0
    const arcs = segments
      .map((seg, i) => {
        const pct = (seg.value / total) * 100
        const dash = Math.max(0, pct - GAP)
        const circle = `
          <circle class="tbw-mica__seg tbw-mica__arc" data-index="${i}"
            cx="100" cy="100" r="70" fill="none"
            stroke="${seg.color}" stroke-width="26" pathLength="100"
            stroke-dasharray="${dash} ${100 - dash}"
            stroke-dashoffset="${-offset}" />`
        offset += pct
        return circle
      })
      .join("")
    return `<svg class="tbw-mica__svg tbw-mica__donut" viewBox="0 0 200 200" role="img">
      <g transform="rotate(-90 100 100)">${arcs}</g>
    </svg>`
  }

  /** Full pie chart (filled wedges) — used for the sector breakdown. */
  private pieSvg(segments: Segment[], total: number): string {
    const cx = 100
    const cy = 100
    const r = 96
    const point = (angleDeg: number) => {
      const a = ((angleDeg - 90) * Math.PI) / 180 // 0% starts at top
      return [cx + r * Math.cos(a), cy + r * Math.sin(a)]
    }
    let acc = 0
    const wedges = segments
      .map((seg, i) => {
        const startAngle = (acc / total) * 360
        acc += seg.value
        const endAngle = (acc / total) * 360
        const [x0, y0] = point(startAngle)
        const [x1, y1] = point(endAngle)
        const largeArc = endAngle - startAngle > 180 ? 1 : 0
        const d = `M ${cx} ${cy} L ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`
        return `<path class="tbw-mica__seg tbw-mica__wedge" data-index="${i}" d="${d}" fill="${seg.color}" />`
      })
      .join("")
    return `<svg class="tbw-mica__svg tbw-mica__pie" viewBox="0 0 200 200" role="img">${wedges}</svg>`
  }

  // ── Cost bar chart (static editorial ranges) ─────────────────────────────────
  private costCardHtml(stats: MicaStats): string {
    const items = stats.cost.items
    const AXIS_MAX = 400_000
    const ticks = [0, 100_000, 200_000, 300_000, 400_000]
    const H = 320 // plot height in svg units
    const y = (v: number) => H - (v / AXIS_MAX) * H

    const grid = ticks
      .map((t) => `<g><line x1="0" x2="900" y1="${y(t)}" y2="${y(t)}" class="tbw-mica__gridline"/>
        <text x="-12" y="${y(t) + 4}" class="tbw-mica__axis">€${t / 1000}k</text></g>`)
      .join("")

    const colW = 900 / items.length
    const barW = 150
    const bars = items
      .map((it, i) => {
        const cx = colW * i + colW / 2
        const topY = y(it.max)
        const botY = y(it.min)
        return `
        <g class="tbw-mica__cost-col" transform="translate(${cx - barW / 2},0)">
          <rect class="tbw-mica__bar" x="0" y="${topY}" width="${barW}" height="${botY - topY}" rx="14"/>
          <text class="tbw-mica__bar-max" x="${barW / 2}" y="${topY - 14}">Max : €${Math.round(it.max / 1000)}k</text>
          <text class="tbw-mica__bar-min" x="${barW / 2}" y="${botY + 26}">Min : €${Math.round(it.min / 1000)}k</text>
          <text class="tbw-mica__bar-label" x="${barW / 2}" y="${H + 42}">${this.wrapTspans(it.label, barW / 2)}</text>
        </g>`
      })
      .join("")

    return `
      <article class="tbw-mica__card">
        ${this.brandHeader(
          "COST OF COMPLIANCE",
          "Cost to obtain & maintain a MiCA licence",
          "Cost ranges are indicative and can vary significantly depending on the applicant’s level of regulatory maturity, existing compliance infrastructure, internal staffing capabilities, and choice of external legal and compliance advisors. Organisations that are already regulated and have established governance and compliance functions typically face substantially lower implementation costs than firms building these capabilities from scratch."
        )}
        <div class="tbw-mica__cost-plot">
          <svg viewBox="-60 -30 990 470" role="img">
            ${grid}
            ${bars}
          </svg>
        </div>
        ${this.footer("Source: The Big Whale estimates")}
      </article>`
  }

  /** Greedy word-wrap into centered <tspan> lines for SVG bar labels. */
  private wrapTspans(label: string, x: number, maxChars = 24): string {
    const words = label.split(" ")
    const lines: string[] = []
    let cur = ""
    for (const w of words) {
      if (!cur) cur = w
      else if ((cur + " " + w).length <= maxChars) cur += " " + w
      else { lines.push(cur); cur = w }
    }
    if (cur) lines.push(cur)
    return lines.map((ln, i) => `<tspan x="${x}" dy="${i === 0 ? 0 : 18}">${ln}</tspan>`).join("")
  }

  // ── Interactivity: hover sync between arcs, legend rows and the tooltip ───────
  private wireInteractions() {
    this.querySelectorAll<HTMLElement>(".tbw-mica__card").forEach((card) => {
      const tooltip = card.querySelector<HTMLElement>(".tbw-mica__tooltip")
      const segs = Array.from(card.querySelectorAll<SVGElement>(".tbw-mica__seg"))
      const rows = Array.from(card.querySelectorAll<HTMLElement>(".tbw-mica__legend-row"))
      if (!segs.length) return
      const total = Number(card.dataset.total || "0") || 1

      const setActive = (idx: number | null) => {
        segs.forEach((s) => s.classList.toggle("is-active", Number((s as any).dataset.index) === idx))
        rows.forEach((r) => r.classList.toggle("is-active", Number(r.dataset.index) === idx))
      }

      const showTip = (idx: number, clientX: number, clientY: number) => {
        const row = rows[idx]
        if (!row || !tooltip) return
        const label = row.querySelector(".tbw-mica__legend-label strong")?.textContent ?? ""
        const count = row.querySelector(".tbw-mica__legend-count")?.textContent ?? ""
        const pct = Math.round((Number(count.replace(/\D/g, "")) / total) * 100)
        tooltip.innerHTML = `<strong>${label}</strong> ${count} <span>(${pct}%)</span>`
        tooltip.hidden = false
        const rect = card.getBoundingClientRect()
        tooltip.style.left = `${clientX - rect.left}px`
        tooltip.style.top = `${clientY - rect.top}px`
      }
      const hideTip = () => {
        if (tooltip) tooltip.hidden = true
        setActive(null)
      }

      const bind = (el: HTMLElement | SVGElement, idx: number) => {
        el.addEventListener("mouseenter", (e) => {
          setActive(idx)
          showTip(idx, (e as MouseEvent).clientX, (e as MouseEvent).clientY)
        })
        el.addEventListener("mousemove", (e) => showTip(idx, (e as MouseEvent).clientX, (e as MouseEvent).clientY))
        el.addEventListener("mouseleave", hideTip)
      }
      segs.forEach((s) => bind(s, Number((s as any).dataset.index)))
      rows.forEach((r) => bind(r, Number(r.dataset.index)))
    })
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────
  private fmt(n: number): string {
    return new Intl.NumberFormat("en-US").format(n)
  }

}
